import { Link } from "react-router-dom";
import { Phone, Languages } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { LANGS } from "@/lib/i18n";
import { UkGlyph } from "@/components/UkGlyph";
import { openCookieSettings } from "@/components/CookieNotice";

export default function Footer() {
  const { t, lang, changeLang } = useApp();
  return (
    <footer data-print-hide className="mt-16 border-t border-slate-200 bg-navy text-slate-200">
      <div className="container-x py-12 grid gap-10 md:grid-cols-4">
        <div>
          <div className="flex items-center gap-2 font-heading font-extrabold text-xl text-white"><span className="flex h-8 w-8 items-center justify-center rounded-md bg-unionblue text-white ring-1 ring-inset ring-white/15"><UkGlyph className="h-5 w-5" /></span> UK Ready</div>
          <p className="mt-2 text-sm text-slate-400 leading-relaxed">{t("footer.tag")}</p>
          <div className="mt-4 rounded-md bg-red-900/40 border border-red-700 p-3 text-sm">
            <div className="flex items-center gap-2 font-semibold text-white"><Phone className="h-4 w-4" /> {t("footer.emergency")}</div>
            <div className="text-slate-300 text-xs mt-1">{t("footer.numbers")}</div>
          </div>
        </div>
        <FooterCol title={t("footer.col.explore")} links={[["/prepare", "Prepare"], ["/live", "UK Situation Room"], ["/report", "Report it"], ["/packs", "Tailored guides"], ["/updates", "Latest updates"], ["/evidence", "How ready is the UK?"], ["/my-plan", "My household plan"], ["/local", "Local information"], ["/local/resilience", "Local Resilience Forums"]]} />
        <FooterCol title={t("footer.col.tools")} links={[["/guides", "All guides"], ["/faq", "Questions answered"], ["/food-alerts", "Food alerts"], ["/water", "Water supply problems"], ["/tools", "Household tools"], ["/worksheets", "Printable worksheets"], ["/downloads", "Downloads & handbook"], ["/community", "Community"], ["/business", "Business"], ["/organisations", "For organisations"], ["/verify", "Is this reliable?"], ["/lite", "Low-data version"], ["/api/text", "Text-only guides"]]} />
        <FooterCol title={t("footer.col.about")} links={[["/about", "About us"], ["/how-we-source-information", "How we source information"], ["/editorial-standards", "Editorial standards"], ["/live-data-sources", "Live data sources"], ["/corrections", "Corrections"], ["/accessibility", "Accessibility"], ["/privacy", "Privacy"], ["/disclaimer", "Disclaimer"], ["/terms", "Terms of use"], ["/contact", "Contact"], ["/admin/login", "Editor sign in"]]} />
      </div>
      <div className="border-t border-slate-800">
        <div className="container-x py-4 text-xs text-slate-400 flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
          <p className="flex-1">{t("footer.legal")}</p>
          <div className="flex items-center gap-4 shrink-0">
            <label className="inline-flex items-center gap-1.5">
              <Languages className="h-4 w-4 text-slate-400" />
              <select data-testid="footer-lang-select" value={lang} onChange={(e) => changeLang(e.target.value)} aria-label={t("lang.aria")}
                className="rounded-md border border-slate-700 bg-navy px-2 py-1 text-xs font-semibold text-slate-200">
                {LANGS.map((l) => <option key={l.code} value={l.code} className="text-slate-900">{l.label}</option>)}
              </select>
            </label>
            <button data-testid="footer-cookie-settings" onClick={openCookieSettings} className="underline hover:text-white">{t("cookie.settings")}</button>
            <p>© {new Date().getFullYear()} UK Ready (working name)</p>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <div className="label-caps text-slate-400 mb-3">{title}</div>
      <ul className="space-y-2 text-sm">
        {links.map(([to, l]) => <li key={to}><Link to={to} className="text-slate-300 hover:text-white hover:underline">{l}</Link></li>)}
      </ul>
    </div>
  );
}
